import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Save, RefreshCw, CheckCircle, AlertCircle, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { cheerJamboree as defaultCheer } from '../../mock';
import { ensureUids, stripUids, withUid } from '../../hooks/useArrayFieldIds';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const hydrate = (raw) => ({
  ...raw,
  program: ensureUids(raw.program),
  contacts: ensureUids(raw.contacts),
});

const dehydrate = (raw) => ({
  ...raw,
  program: stripUids(raw.program),
  contacts: stripUids(raw.contacts),
});

const INPUT_STYLE = { width: '100%' };
const FIELD_GRID_STYLE = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: '0.75rem',
};

const SectionCard = ({ title, children }) => (
  <Card style={{ marginBottom: '1rem' }}>
    <CardHeader>
      <CardTitle style={{ color: '#FFD700' }}>{title}</CardTitle>
    </CardHeader>
    <CardContent>{children}</CardContent>
  </Card>
);

const LabeledField = ({ label, children, fullWidth }) => (
  <label style={{ color: '#fff', display: 'block', ...(fullWidth ? { gridColumn: '1 / -1' } : {}) }}>
    <span style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.25rem', color: '#d4d4d4' }}>
      {label}
    </span>
    {children}
  </label>
);

const CheerEditorTab = () => {
  const [event, setEvent] = useState(defaultCheer);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/event/cheer`);
      const data = await res.json();
      setEvent(hydrate(data.event ? { ...defaultCheer, ...data.event } : defaultCheer));
    } catch (err) {
      setStatus({ type: 'error', message: `Load failed: ${err.message}` });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const patch = (key) => (value) => setEvent((e) => ({ ...e, [key]: value }));

  const updateArrayItem = (key, idx, next) => setEvent((e) => {
    const arr = [...(e[key] || [])];
    arr[idx] = { ...next, _uid: arr[idx]?._uid || next._uid };
    return { ...e, [key]: arr };
  });
  const removeArrayItem = (key, idx) => setEvent((e) => {
    const arr = [...(e[key] || [])];
    arr.splice(idx, 1);
    // Re-number program steps to keep them contiguous
    if (key === 'program') {
      arr.forEach((it, i) => { it.step = i + 1; });
    }
    return { ...e, [key]: arr };
  });
  const addArrayItem = (key, blank) => setEvent((e) => {
    const next = [...(e[key] || []), withUid(blank)];
    if (key === 'program') {
      next.forEach((it, i) => { it.step = i + 1; });
    }
    return { ...e, [key]: next };
  });

  const save = async () => {
    setSaving(true);
    setStatus(null);
    try {
      const res = await fetch(`${API_URL}/api/event/cheer`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dehydrate(event)),
      });
      const data = await res.json();
      if (data.success) {
        setStatus({ type: 'success', message: 'Cheer event saved! Live on the site in a moment.' });
      } else {
        setStatus({ type: 'error', message: data.detail || 'Save failed.' });
      }
    } catch (err) {
      setStatus({ type: 'error', message: `Save failed: ${err.message}` });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: '#d4d4d4' }}>
        <RefreshCw className="animate-spin" /> Loading cheer event…
      </div>
    );
  }

  return (
    <div data-testid="cheer-editor-tab" style={{ padding: '0 0.5rem' }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem',
      }}>
        <h2 style={{ color: '#FFD700', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Sparkles size={22} /> Cheer Jamboree — Editor
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Button onClick={load} className="import-btn" data-testid="cheer-editor-reload-btn">
            <RefreshCw size={16} /> Reload
          </Button>
          <Button onClick={save} disabled={saving} className="send-email-btn" data-testid="cheer-editor-save-btn">
            {saving ? <><RefreshCw size={16} className="animate-spin" /> Saving…</> : <><Save size={16} /> Save Changes</>}
          </Button>
        </div>
      </div>

      {status && (
        <div className={`reply-status ${status.type}`} data-testid="cheer-editor-status" style={{ marginBottom: '1rem' }}>
          {status.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
          {status.message}
        </div>
      )}

      {/* Basics */}
      <SectionCard title="Basics">
        <div style={FIELD_GRID_STYLE}>
          <LabeledField label="Title *" fullWidth>
            <input className="email-input" style={INPUT_STYLE} value={event.title} onChange={(e) => patch('title')(e.target.value)} data-testid="cheer-title" />
          </LabeledField>
          <LabeledField label="Subtitle">
            <input className="email-input" style={INPUT_STYLE} value={event.subtitle || ''} onChange={(e) => patch('subtitle')(e.target.value)} />
          </LabeledField>
          <LabeledField label="Tagline">
            <input className="email-input" style={INPUT_STYLE} value={event.tagline || ''} onChange={(e) => patch('tagline')(e.target.value)} data-testid="cheer-tagline" />
          </LabeledField>
          <LabeledField label="Date">
            <input className="email-input" style={INPUT_STYLE} value={event.date || ''} onChange={(e) => patch('date')(e.target.value)} />
          </LabeledField>
          <LabeledField label="Venue">
            <input className="email-input" style={INPUT_STYLE} value={event.venue || ''} onChange={(e) => patch('venue')(e.target.value)} />
          </LabeledField>
          <LabeledField label="City / Address">
            <input className="email-input" style={INPUT_STYLE} value={event.city || ''} onChange={(e) => patch('city')(e.target.value)} />
          </LabeledField>
          <LabeledField label="Contact Email">
            <input className="email-input" style={INPUT_STYLE} value={event.email || ''} onChange={(e) => patch('email')(e.target.value)} />
          </LabeledField>
          <LabeledField label="Intro / Description" fullWidth>
            <textarea className="email-textarea" style={INPUT_STYLE} rows={3} value={event.intro || ''} onChange={(e) => patch('intro')(e.target.value)} />
          </LabeledField>
        </div>
      </SectionCard>

      {/* Program */}
      <SectionCard title="Program (5 spotlight moments — steps renumber automatically)">
        {(event.program || []).map((p, i) => (
          <div key={i} style={{ border: '1px solid rgba(255,215,0,0.2)', borderRadius: '10px', padding: '0.75rem', marginBottom: '0.6rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <span style={{ color: '#FFD700', fontWeight: 800, fontSize: '0.85rem' }}>STEP {p.step || i + 1}</span>
              <button type="button" onClick={() => removeArrayItem('program', i)} className="delete-btn" data-testid={`program-remove-${i}`} style={{ marginLeft: 'auto' }}>
                <Trash2 size={14} />
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <input className="email-input" placeholder="📣" value={p.emoji || ''} onChange={(e) => updateArrayItem('program', i, { ...p, emoji: e.target.value })} data-testid={`program-emoji-${i}`} />
              <input className="email-input" placeholder="Title" value={p.title || ''} onChange={(e) => updateArrayItem('program', i, { ...p, title: e.target.value })} data-testid={`program-title-${i}`} />
            </div>
            <input className="email-input" placeholder="What (short summary)" value={p.what || ''} onChange={(e) => updateArrayItem('program', i, { ...p, what: e.target.value })} style={{ width: '100%', marginBottom: '0.5rem' }} data-testid={`program-what-${i}`} />
            <textarea className="email-textarea" placeholder="Detail (full description)" rows={2} value={p.detail || ''} onChange={(e) => updateArrayItem('program', i, { ...p, detail: e.target.value })} style={{ width: '100%' }} data-testid={`program-detail-${i}`} />
          </div>
        ))}
        <Button onClick={() => addArrayItem('program', { emoji: '', title: '', what: '', detail: '' })} className="import-btn" data-testid="program-add-btn">
          <Plus size={16} /> Add Program Step
        </Button>
      </SectionCard>

      {/* Contacts */}
      <SectionCard title="Cheer Coach Contacts">
        {(event.contacts || []).map((c, i) => (
          <div key={c._uid} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <input className="email-input" placeholder="Coach Name" value={c.name} onChange={(e) => updateArrayItem('contacts', i, { ...c, name: e.target.value })} data-testid={`cheer-contact-name-${i}`} />
            <input className="email-input" placeholder="(XXX) XXX-XXXX" value={c.phone} onChange={(e) => updateArrayItem('contacts', i, { ...c, phone: e.target.value })} data-testid={`cheer-contact-phone-${i}`} />
            <button type="button" onClick={() => removeArrayItem('contacts', i)} className="delete-btn" data-testid={`cheer-contact-remove-${i}`}>
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        <Button onClick={() => addArrayItem('contacts', { name: '', phone: '' })} className="import-btn" data-testid="cheer-contact-add-btn">
          <Plus size={16} /> Add Coach
        </Button>
      </SectionCard>
    </div>
  );
};

export default CheerEditorTab;
